import React, { useState, useEffect, useLayoutEffect } from 'react';
import { BrowserRouter, Routes, Route, Link, useSearchParams, useLocation } from 'react-router-dom';
import {
  Users, ChevronRight, CheckCircle2, Zap, ShieldCheck,
  Mail, BarChart3, Layout, Star, Coffee, Heart, Crown, LogIn,
  ArrowRight, LayoutDashboard, Calculator, Megaphone, FileText,
  X
} from 'lucide-react';
import Documentation from './pages/Documentation';
import Contact from './pages/Contact';
import Faq from './pages/Faq';
import { RemoteConfigProvider, useRemoteConfig } from './contexts/RemoteConfigContext';

import MaintenancePage from './components/MaintenancePage';
import { MentionsLegales, Confidentialite, CGU } from './pages/LegalPages';
import MainLayout from './components/MainLayout';
import { PLAN_CATEGORIES } from './constants/planFeatures';
import StructuredData from './components/StructuredData';
import { SEO_CONTENT } from './constants/seoData';

const FEATURES_DATA = [
  {
    id: 'bureau',
    label: 'Bureau',
    title: "C’est qui qui fait quoi ?",
    desc: "Assignez les tâches en un clic pendant vos réunions. Ne perdez plus jamais le fil de qui devait ramener le café ou réserver la salle.",
    points: ['Prise de note en direct', 'Distribution des tâches', 'Suivi automatique agenda'],
    footer: "« Qui devait faire ça ? Ah... c'est moi selon le dernier CR ! »",
    icon: LayoutDashboard,
    color: 'var(--color-mint)',
    pain: "Répartir les tâches sans que personne n'oublie",
  },
  {
    id: 'repertoire',
    label: 'Répertoire',
    title: <span>Adieu le Fichier Excel "adhérents_202<span className="opacity-20">5</span>6(copie)"</span>,
    desc: "Fini les doublons et les mails périmés. Votre annuaire est vivant, connecté à votre réseau et même à votre inventaire.",
    points: ['Gestion des membres', 'Annuaire du réseau', 'Suivi de l\'inventaire'],
    footer: "Parce que vos membres ne sont pas que des lignes de tableau.",
    icon: Users,
    color: 'var(--color-teal)',
    pain: "Gérer mes membres sans un tableur Excel infernal",
  },
  {
    id: 'compta',
    label: 'Comptabilité',
    title: "Votre Trésorier va vous Aimer",
    desc: "Simplifiez-lui la vie. Saisissez vos mouvements simplement et laissez Superasso générer le Bilan et le Compte de Résultat.",
    points: ['Mouvements & Factures', 'Budget Prévisionnel', 'Bilan et Compte de Résultat automatiques'],
    footer: "C'est qui qui sera fier de vous montrer toutes ces belles couleurs dans la liste des mouvements ?",
    icon: Calculator,
    color: 'var(--color-clay)',
    pain: "Faire le bilan sans être expert comptable",
  },
  {
    id: 'comm',
    label: 'Communication',
    title: "Devenez une Star (Locale)",
    desc: "Fini de prêcher dans le désert. Newsletters, Page de liens et Kit Presse : rayonnez auprès de votre communauté en 5 minutes.",
    points: ['Newsletters percutantes', 'Page de liens publique', 'Kit Presse professionnel'],
    footer: "Faites du bruit, on s'occupe de l'écho.",
    icon: Megaphone,
    color: 'var(--color-purple)',
    pain: "Envoyer des newsletters qu'on a envie de lire",
  },
  {
    id: 'admin',
    label: 'Administratif',
    title: "Statuts & Règlements : Le droit au bonheur.",
    desc: "Parce que vous préférez sûrement organiser un tournoi de pétanque plutôt que de débattre sur l'alinéa 4 de l'article B. Posez les fondations juridiques de votre asso sans y laisser votre santé mentale.",
    points: [
      'Assistant de rédaction (on vous tient la main)',
      'Lien public (partagez vos statuts en 1 clic)'
    ],
    footer: "Garanti 100% sans migraine, promis.",
    icon: FileText,
    color: 'var(--color-rose)',
    pain: "Arrêter de chercher le dossier 'STATUTS_FINAL_V2_MAJ.doc' partout",
  }
];

function FeatureShowcase() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    if (isPaused) return;

    const interval = setInterval(() => {
      setActiveIndex((current) => (current + 1) % FEATURES_DATA.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isPaused]);

  const activeFeature = FEATURES_DATA[activeIndex];

  return (
    <div className="flex flex-col gap-12">
      {/* Header Row */}
      <div className="flex flex-col gap-3 text-center mb-16">
        <p className="text-[#5BC0AF] font-bold text-xs uppercase tracking-[0.3em] mb-4">Outils</p>
        <h2 className="text-4xl md:text-6xl font-bold tracking-tight mb-6 leading-[1.1]">
          C'est quoi votre douleur <br />
          <span className="text-[#5BC0AF]">en réunion d'asso ?</span>
        </h2>
        <p className="text-[#F5F3EE]/40 font-medium text-base max-w-2xl mx-auto">
          Cliquez pour découvrir comment Superasso vous soulage.
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-8 items-stretch">
        {/* Left Column: Pain Points */}
        <div className="grid grid-cols-5 md:flex md:flex-col gap-2 md:gap-3">
          {FEATURES_DATA.map((f, idx) => (
            <button
              key={f.id}
              onClick={() => {
                setActiveIndex(idx);
                setIsPaused(true);
              }}
              onMouseEnter={() => setIsPaused(true)}
              onMouseLeave={() => setIsPaused(false)}
              className={`p-2 py-3 md:p-5 flex-1 rounded-2xl md:rounded-[1.5rem] border transition-all duration-500 text-left group relative overflow-hidden flex items-center justify-center md:justify-start ${idx === activeIndex
                ? 'bg-white/10 border-white/20 shadow-xl'
                : 'bg-transparent border-white/5 hover:bg-white/5'
                }`}
            >
              {/* Progress bar line */}
              {idx === activeIndex && !isPaused && (
                <div
                  className="absolute bottom-0 left-0 h-1 bg-[#5BC0AF] animate-progress"
                  style={{ animationDuration: '5s' }}
                />
              )}

              <div className="flex items-center gap-0 md:gap-4 w-full justify-center md:justify-start">
                <div
                  className={`w-8 h-8 md:w-10 md:h-10 shrink-0 rounded-xl flex items-center justify-center transition-all duration-500 ${idx === activeIndex ? 'bg-white/20 rotate-0' : 'bg-white/5 -rotate-6 opacity-40 group-hover:opacity-100 group-hover:rotate-0'
                    }`}
                >
                  <f.icon size={20} style={{ color: idx === activeIndex ? f.color : 'white' }} />
                </div>
                <span className={`hidden md:block text-base md:text-lg font-bold transition-all duration-500 ${idx === activeIndex ? 'text-white' : 'text-[#F5F3EE]/30 group-hover:text-[#F5F3EE]/60'
                  }`}>
                  {f.pain}
                </span>

                {idx === activeIndex && (
                  <div className="hidden md:block ml-auto animate-pulse">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#5BC0AF]" />
                  </div>
                )}
              </div>
            </button>
          ))}
        </div>

        {/* Right Column: Feature Card */}
        <div
          className="relative"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          <div
            key={activeIndex}
            className="animate-fade-in-up h-full"
          >
            <div className="settle-card !p-8 md:!p-10 group h-full flex flex-col gap-8 relative overflow-hidden border-[#5BC0AF]/20 bg-white/[0.04]">
              <div
                className="absolute -top-40 -right-40 w-96 h-96 opacity-10 blur-[120px] pointer-events-none rounded-full transition-all duration-1000"
                style={{ backgroundColor: activeFeature.color }}
              />

              <div className="flex flex-col gap-6 flex-1 relative z-10">
                <div className="flex flex-col gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center shrink-0 shadow-inner">
                      <activeFeature.icon size={24} style={{ color: activeFeature.color }} />
                    </div>
                    <div className="flex flex-col">
                      <span className="text-[10px] font-black uppercase tracking-[0.3em] text-[#5BC0AF] mb-0.5">Module</span>
                      <span className="text-[10px] font-bold uppercase tracking-[0.2em] opacity-40 leading-none">{activeFeature.label}</span>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-2xl md:text-3xl font-black mb-4 leading-tight min-h-[4rem] flex items-center drop-shadow-sm">
                    {activeFeature.title}
                  </h3>
                  <p className="text-[#F5F3EE]/60 text-base leading-relaxed mb-6 font-medium italic">
                    "{activeFeature.desc}"
                  </p>

                  <div className="space-y-4">
                    <p className="text-[10px] font-black uppercase tracking-[0.3em] text-[#5BC0AF]/40">Atouts majeurs</p>
                    <ul className="grid gap-2.5">
                      {activeFeature.points.map((pt, i) => (
                        <li key={i} className="flex items-center gap-3 text-sm text-[#F5F3EE]/70 font-semibold group/item">
                          <div className="w-1.5 h-1.5 rounded-full shrink-0 transition-transform group-hover/item:scale-150" style={{ backgroundColor: activeFeature.color }} />
                          {pt}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>

              <div className="pt-6 border-t border-white/10 relative z-10 mt-auto">
                <div className="flex items-center gap-3">
                  <div className="w-6 h-px bg-white/10" />
                  <p className="text-xs font-bold italic opacity-80" style={{ color: activeFeature.color }}>
                    {activeFeature.footer}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


const SHOWCASE_IMAGES = [
  { src: '/carrousel/dashboard.png', caption: 'Tableau de bord' },
  { src: '/carrousel/mouvements.png', caption: 'Suivi des mouvements' },
  { src: '/carrousel/compte-resultat.png', caption: 'Compte de résultat' },
  { src: '/carrousel/bilan.png', caption: 'Bilan' },
  { src: '/carrousel/membres.png', caption: 'Annuaire Membres' },
  { src: '/carrousel/cr-reunion.png', caption: 'Compte-rendus Réunions' },
];

function Lightbox({ image, onClose }) {
  useEffect(() => {
    if (!image) return;

    const handleEsc = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = 'unset';
    };
  }, [image, onClose]);

  if (!image) return null;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4 md:p-12"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/20 backdrop-blur-[30px] animate-fade-in" />

      <button
        onClick={onClose}
        className="absolute top-8 right-8 text-white/50 hover:text-white transition-all hover:scale-110 z-10 p-2"
      >
        <X size={40} />
      </button>

      <div
        className="relative flex flex-col items-center gap-6 max-w-full md:max-w-6xl animate-zoom-in"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="rounded-2xl border border-white/10 shadow-[0_0_100px_rgba(0,0,0,0.5)] overflow-hidden bg-black/20">
          <img
            src={image.src}
            alt={image.caption}
            className="w-full h-auto max-h-[80vh] object-contain"
          />
        </div>
        <p className="text-xl md:text-2xl font-black uppercase tracking-[0.3em] text-white text-center drop-shadow-lg">
          {image.caption}
        </p>
      </div>
    </div>
  );
}

function ImageMarquee() {
  const scrollRef = React.useRef(null);
  const [isDragging, setIsDragging] = React.useState(false);
  const [startX, setStartX] = React.useState(0);
  const [scrollLeft, setScrollLeft] = React.useState(0);
  const [autoScroll, setAutoScroll] = React.useState(true);
  const [selectedImage, setSelectedImage] = useState(null);

  React.useEffect(() => {
    if (!autoScroll || isDragging) return;

    const scroll = () => {
      if (scrollRef.current) {
        scrollRef.current.scrollLeft += 0.8;
        // Infinite wrap logic with 3 sets of images
        if (scrollRef.current.scrollLeft >= (scrollRef.current.scrollWidth / 3) * 2) {
          scrollRef.current.scrollLeft = scrollRef.current.scrollWidth / 3;
        }
      }
    };

    const interval = setInterval(scroll, 16);
    return () => clearInterval(interval);
  }, [autoScroll, isDragging]);

  // Initial scroll position to the middle set
  React.useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollLeft = scrollRef.current.scrollWidth / 3;
    }
  }, []);

  const onMouseDown = (e) => {
    setIsDragging(true);
    setStartX(e.pageX - scrollRef.current.offsetLeft);
    setScrollLeft(scrollRef.current.scrollLeft);
  };

  const onMouseUp = () => {
    setIsDragging(false);
    // Pause auto-scroll briefly after dragging
    setTimeout(() => setAutoScroll(true), 2000);
  };

  const onMouseMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    setAutoScroll(false);
    const x = e.pageX - scrollRef.current.offsetLeft;
    const walk = (x - startX) * 2;
    scrollRef.current.scrollLeft = scrollLeft - walk;

    // Bounds check for infinite wrap while dragging
    if (scrollRef.current.scrollLeft >= (scrollRef.current.scrollWidth / 3) * 2) {
      scrollRef.current.scrollLeft = scrollRef.current.scrollWidth / 3;
      setStartX(e.pageX - scrollRef.current.offsetLeft);
      setScrollLeft(scrollRef.current.scrollLeft);
    } else if (scrollRef.current.scrollLeft <= scrollRef.current.scrollWidth / 3) {
      scrollRef.current.scrollLeft = (scrollRef.current.scrollWidth / 3) * 2;
      setStartX(e.pageX - scrollRef.current.offsetLeft);
      setScrollLeft(scrollRef.current.scrollLeft);
    }
  };

  return (
    <div className="py-12 bg-black/5 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 mb-12">
        <div className="h-px w-full bg-gradient-to-r from-transparent via-white/10 to-transparent" />
      </div>

      <div
        className="marquee-container relative cursor-grab active:cursor-grabbing"
        onMouseEnter={() => setAutoScroll(false)}
        onMouseLeave={() => !isDragging && setAutoScroll(true)}
      >
        <div
          ref={scrollRef}
          className="flex gap-8 overflow-hidden py-10 select-none"
          onMouseDown={onMouseDown}
          onMouseUp={onMouseUp}
          onMouseLeave={onMouseUp}
          onMouseMove={onMouseMove}
        >
          {/* Use 3 sets of images for seamless infinite drag & scroll */}
          {[...SHOWCASE_IMAGES, ...SHOWCASE_IMAGES, ...SHOWCASE_IMAGES].map((img, idx) => (
            <div
              key={idx}
              className="flex-shrink-0 group flex flex-col gap-4 items-center cursor-pointer"
              onClick={() => setSelectedImage(img)}
            >
              <div className="relative">
                {/* Subtle Offset Glow */}
                <div className="absolute inset-0 bg-[#5BC0AF]/40 translate-x-3 translate-y-3 blur-[2px]" />

                <div className="h-[200px] md:h-[320px] overflow-hidden border border-white/10 shadow-2xl relative z-10 bg-[#24574E]">
                  <img
                    src={img.src}
                    alt={img.caption}
                    className="h-full w-auto object-contain transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                </div>
              </div>
              <p className="text-[10px] font-black uppercase tracking-[0.3em] text-[#F5F3EE]/30 group-hover:text-[#5BC0AF] transition-colors duration-500">
                {img.caption}
              </p>
            </div>
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 mt-12">
        <div className="h-px w-full bg-gradient-to-r from-transparent via-white/10 to-transparent" />
      </div>

      {selectedImage && (
        <Lightbox
          image={selectedImage}
          onClose={() => setSelectedImage(null)}
        />
      )}
    </div>
  );
}

const APP_URL = (import.meta.env.VITE_APP_URL && import.meta.env.VITE_APP_URL !== 'undefined')
  ? import.meta.env.VITE_APP_URL
  : (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
    ? 'http://localhost:5173'
    : 'https://app.superasso.fr';

function FeatureValue({ value, color }) {
  if (typeof value === 'boolean') {
    return value ? (
      <div className="flex justify-center">
        <CheckCircle2 size={18} style={{ color }} />
      </div>
    ) : (
      <div className="flex justify-center opacity-20">
        <div className="w-1.5 h-1.5 rounded-full bg-white" />
      </div>
    );
  }
  return (
    <span className="text-sm font-black" style={{ color }}>
      {value}
    </span>
  );
}

// ─── LANDING PAGE ───────────────────────────────────────────
function Landing() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [mobilePricingTab, setMobilePricingTab] = useState('free');
  const [pricingIsPaused, setPricingIsPaused] = useState(false);

  useEffect(() => {
    if (pricingIsPaused) return;
    const interval = setInterval(() => {
      setMobilePricingTab(prev => prev === 'free' ? 'premium' : 'free');
    }, 5000);
    return () => clearInterval(interval);
  }, [pricingIsPaused]);

  useEffect(() => {
    // On ne redirige plus automatiquement vers le dashboard si l'utilisateur est connecté
    // pour lui permettre de consulter le site marketing s'il le souhaite.
  }, []);

  return (
    <div className="bg-[#24574E] font-body text-[#F5F3EE] selection:bg-[#F9D965]/20 selection:text-[#F9D965]">
      <StructuredData />

      {/* Hero */}
      <section className="relative pt-48 pb-36 px-6 flex flex-col items-center text-center overflow-hidden">
        {/* Settle-style Glow Gradient (More Contrasted) */}
        <div className="absolute top-[35%] left-1/2 -translate-x-1/2 -translate-y-1/2 w-[140%] lg:w-[100rem] h-[60rem] bg-radial from-[#5BC0AF]/20 via-[#5BC0AF]/5 to-transparent opacity-80 pointer-events-none" />

        {/* Orbit Background Simulation */}
        <div className="absolute top-[45%] left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] pointer-events-none opacity-20">
          <div className="absolute inset-0 border border-white/10 rounded-full" />
          <div className="absolute inset-[15%] border border-white/10 rounded-full" />

          <div className="absolute top-1/2 left-1/2 w-4 h-4 bg-[#F9D965] rounded-full -translate-x-1/2 -translate-y-1/2 shadow-[0_0_20px_#F9D965]" />

          {/* Rotating highlight icons - distributed with animation delays */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 p-4 bg-white/10 rounded-2xl border border-white/20 backdrop-blur-md animate-orbit shadow-lg" style={{ animationDelay: '-1s' }}>
            <Users size={24} className="text-[#5BC0AF]" />
          </div>
          <div className="absolute top-0 left-1/2 -translate-x-1/2 p-4 bg-white/10 rounded-2xl border border-white/20 backdrop-blur-md animate-orbit shadow-lg" style={{ animationDelay: '-3s' }}>
            <BarChart3 size={24} className="text-[#D3997F]" />
          </div>
          <div className="absolute top-0 left-1/2 -translate-x-1/2 p-4 bg-white/10 rounded-2xl border border-white/20 backdrop-blur-md animate-orbit shadow-lg" style={{ animationDelay: '-5s' }}>
            <Mail size={24} className="text-[#F9D965]" />
          </div>
          <div className="absolute top-0 left-1/2 -translate-x-1/2 p-4 bg-white/10 rounded-2xl border border-white/20 backdrop-blur-md animate-orbit shadow-lg" style={{ animationDelay: '-7s' }}>
            <ShieldCheck size={24} className="text-[#5BC0AF]" />
          </div>
          <div className="absolute top-0 left-1/2 -translate-x-1/2 p-4 bg-white/10 rounded-2xl border border-white/20 backdrop-blur-md animate-orbit shadow-lg" style={{ animationDelay: '-9s' }}>
            <Zap size={24} className="text-[#D3997F]" />
          </div>
          <div className="absolute top-0 left-1/2 -translate-x-1/2 p-4 bg-white/10 rounded-2xl border border-white/20 backdrop-blur-md animate-orbit shadow-lg" style={{ animationDelay: '-11s' }}>
            <Heart size={24} className="text-[#F9D965]" />
          </div>
          <div className="absolute top-0 left-1/2 -translate-x-1/2 p-4 bg-white/10 rounded-2xl border border-white/20 backdrop-blur-md animate-orbit shadow-lg" style={{ animationDelay: '-13s' }}>
            <Layout size={24} className="text-[#5BC0AF]" />
          </div>
          <div className="absolute top-0 left-1/2 -translate-x-1/2 p-4 bg-white/10 rounded-2xl border border-white/20 backdrop-blur-md animate-orbit shadow-lg" style={{ animationDelay: '-15s' }}>
            <Crown size={24} className="text-[#F9D965]" />
          </div>
        </div>

        <div className="max-w-4xl mx-auto relative z-10">
          <p className="text-[#5BC0AF] font-bold text-xs uppercase tracking-[0.3em] mb-8">
            Pour les bénévoles, par un bénévole.
          </p>
          <h1 className="text-6xl md:text-8xl mb-8 leading-[1.05]">
            Libérez votre <br />
            <span className="italic text-[#5BC0AF]">Énergie Associative</span> aujourd'hui.
          </h1>
          <p className="text-xl text-[#F5F3EE]/70 max-w-2xl mx-auto mb-12 font-medium leading-relaxed">
            La plateforme tout-en-un conçue pour aider les petites assos à faire de grandes choses sans la charge mentale.
          </p>
          <a
            href={`${APP_URL}/profil?mode=signup`}
            className="btn-settle text-lg inline-block text-center"
          >
            Commencez gratuitement
          </a>
        </div>
      </section>

      {/* Social proof */}
      <section className="pb-12 px-6">
        <div className="max-w-5xl mx-auto text-center mb-8">
          <p className="text-sm font-bold uppercase tracking-widest text-[#F9D965]">
            <span className="bg-[#F9D965]/10 px-2 py-1">
              Parce qu'on sait que la vraie vie de toutes les assos,<br /> c'est de savoir qui s'occupe de la buvette et oublier de communiquer à temps.
            </span>
          </p>
        </div>
        <div className="max-w-5xl mx-auto flex flex-wrap justify-center gap-12 opacity-30 grayscale hover:grayscale-0 transition-all duration-700">
          {['⚽️ Sports', '🎲 Jeux', '🎭 Culture', '🏫 Écoles', '🧶 Loisirs', '🦸‍♂️ Super-héros'].map(t => (
            <div key={t} className="flex items-center gap-3 text-lg font-bold tracking-tight">{t}</div>
          ))}
        </div>
      </section>

      {/* Reunion Demo Section */}
      <section id="reunion-demo" className="py-24 px-6 bg-black/10">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-[#5BC0AF] font-bold text-xs uppercase tracking-[0.3em] mb-4">La philosophie SuperAsso</p>
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6">
              Accompagner, générer en un clic <br className="hidden md:block" />
              pour communiquer facilement.
            </h2>
            <p className="text-xl text-[#F5F3EE]/60 font-medium max-w-2xl mx-auto">
              Découvrez comment transformer une réunion de bureau ordinaire en un moment efficace et suivi, en 3 temps simples.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {/* Step 1 */}
            <div className="bg-white/5 border border-white/10 rounded-3xl p-6 flex flex-col gap-6 relative overflow-hidden group hover:border-[#5BC0AF]/30 transition-colors">
              <div className="absolute top-0 right-0 w-32 h-32 bg-[#5BC0AF]/10 blur-[50px] -z-10 group-hover:bg-[#5BC0AF]/20 transition-all" />
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-[#5BC0AF] text-[#24574E] font-black flex items-center justify-center text-xl shrink-0">1</div>
                <h3 className="text-xl font-bold">Préparation & Prise de note</h3>
              </div>
              <p className="text-[#F5F3EE]/70 font-medium">Prenez des notes en direct, assignez des tâches à la volée pendant que tout le monde est réuni.</p>
              <div className="mt-auto rounded-xl overflow-hidden border border-white/10 bg-[#24574E]">
                <img src="/carrousel/reunion1.png" alt="Préparation de la réunion" className="w-full h-auto object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
            </div>

            {/* Step 2 */}
            <div className="bg-white/5 border border-white/10 rounded-3xl p-6 flex flex-col gap-6 relative overflow-hidden group hover:border-[#F9D965]/30 transition-colors">
              <div className="absolute top-0 right-0 w-32 h-32 bg-[#F9D965]/10 blur-[50px] -z-10 group-hover:bg-[#F9D965]/20 transition-all" />
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-[#F9D965] text-[#24574E] font-black flex items-center justify-center text-xl shrink-0">2</div>
                <h3 className="text-xl font-bold">Génération en 1 clic</h3>
              </div>
              <p className="text-[#F5F3EE]/70 font-medium">Générez le compte-rendu automatiquement. Plus besoin de repasser 2h le lendemain à tout mettre au propre.</p>
              <div className="mt-auto rounded-xl overflow-hidden border border-white/10 bg-[#24574E]">
                <img src="/carrousel/reunion2.png" alt="Génération du compte rendu" className="w-full h-auto object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
            </div>

            {/* Step 3 */}
            <div className="bg-white/5 border border-white/10 rounded-3xl p-6 flex flex-col gap-6 relative overflow-hidden group hover:border-[#D3997F]/30 transition-colors">
              <div className="absolute top-0 right-0 w-32 h-32 bg-[#D3997F]/10 blur-[50px] -z-10 group-hover:bg-[#D3997F]/20 transition-all" />
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-[#D3997F] text-[#24574E] font-black flex items-center justify-center text-xl shrink-0">3</div>
                <h3 className="text-xl font-bold">Communication</h3>
              </div>
              <p className="text-[#F5F3EE]/70 font-medium">Communiquez facilement avec toute votre équipe et vos membres via un lien unique et propre.</p>
              <div className="mt-auto rounded-xl overflow-hidden border border-white/10 bg-[#24574E]">
                <img src="/carrousel/reunion3.png" alt="Communication du compte rendu" className="w-full h-auto object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
            </div>
          </div>

          <div className="text-center">
            <a
              href="https://app.superasso.fr/l-asso6/doc/cr/cGlZSShyzJwqyO8N2ea6"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-white/10 border border-white/20 px-8 py-4 rounded-full font-bold hover:bg-white/20 transition-all group"
            >
              <FileText className="text-[#5BC0AF]" size={24} />
              <span>Voir un exemple de Compte-Rendu généré</span>
              <ArrowRight size={20} className="transition-transform group-hover:translate-x-1" />
            </a>
          </div>
        </div>
      </section>

      {/* Modules Carousel */}
      <section id="features" className="pt-24 bg-black/5 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6">


          <FeatureShowcase />

          <div className="mt-12 text-center">
            <Link
              to="/docs"
              className="inline-flex items-center gap-2 text-[#5BC0AF] font-bold hover:gap-3 transition-all group"
            >
              Toutes les fonctionnalités en détail
              <ArrowRight size={20} className="transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      </section>

      <ImageMarquee />


      {/* Pricing Comparison Table */}
      <section id="pricing" className="py-32 px-6 bg-black/20">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-20 animate-fade-in">
            <p className="text-[#5BC0AF] font-bold text-xs uppercase tracking-[0.3em] mb-4">Tarifs</p>
            <h2 className="text-4xl md:text-6xl font-bold tracking-tight mb-6">Un prix unique, sans surprise.</h2>
            <p className="text-xl text-[#F5F3EE]/60 font-medium max-w-2xl mx-auto">
              Pas de calculs savants au nombre de membres. Un socle gratuit et puissant, et un pack Premium pour passer pro.
            </p>
          </div>

          <div
            className="md:hidden flex flex-col mb-8 max-w-sm mx-auto w-full"
          >
            <div className="flex rounded-full bg-white/5 border border-white/10 p-1 relative">
              <button
                onClick={() => {
                  setMobilePricingTab('free');
                  setPricingIsPaused(true);
                }}
                className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-full text-sm font-bold transition-all ${mobilePricingTab === 'free' ? 'bg-white/10 text-white' : 'text-white/50 hover:text-white'
                  }`}
              >
                <Zap size={16} className={mobilePricingTab === 'free' ? 'text-[#5BC0AF]' : 'text-white/50'} />
                L'essentiel
              </button>
              <button
                onClick={() => {
                  setMobilePricingTab('premium');
                  setPricingIsPaused(true);
                }}
                className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-full text-sm font-bold transition-all ${mobilePricingTab === 'premium' ? 'bg-[#F9D965]/10 text-[#F9D965]' : 'text-white/50 hover:text-white'
                  }`}
              >
                <Crown size={16} className={mobilePricingTab === 'premium' ? 'text-[#F9D965]' : 'text-white/50'} />
                Full Premium
              </button>
            </div>
            {/* Progress indicator line */}
            <div className="mt-4 w-full h-1.5 bg-white/5 rounded-full overflow-hidden">
              <div
                key={mobilePricingTab}
                className={`h-full ${pricingIsPaused ? 'w-full' : 'animate-progress'} ${mobilePricingTab === 'free' ? 'bg-[#5BC0AF]' : 'bg-[#F9D965]'}`}
                style={!pricingIsPaused ? { animationDuration: '5s' } : undefined}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-8 max-w-5xl mx-auto items-stretch mb-20">
            {/* ── GRATUIT ── */}
            <div className={`relative rounded-[2rem] md:rounded-[3rem] border border-white/10 bg-white/[0.03] backdrop-blur-sm p-8 md:p-12 flex flex-col items-center text-center group/free hover:border-[#5BC0AF]/30 transition-all duration-500 ${mobilePricingTab !== 'free' ? 'hidden md:flex' : 'flex'}`}>
              <div className="mb-6 md:mb-10 flex-1 flex flex-col items-center">
                <div className="hidden md:flex flex-col items-center gap-2 md:gap-3 mb-6 md:mb-8">
                  <div className="w-10 h-10 md:w-14 md:h-14 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center shrink-0">
                    <Zap className="w-5 h-5 md:w-7 md:h-7 text-[#5BC0AF]" />
                  </div>
                  <div>
                    <p className="text-[8px] md:text-[10px] font-black uppercase tracking-[0.3em] text-[#5BC0AF] mb-1">L'Essentiel</p>
                    <h3 className="text-base md:text-2xl font-black text-white leading-tight">
                      <span className="md:hidden">Gratuite</span>
                      <span className="hidden md:inline">Version Gratuite</span>
                    </h3>
                  </div>
                </div>

                <div className="flex flex-col items-center gap-1 mb-6 md:mb-6 mt-4">
                  <div className="flex items-end">
                    <span className="text-7xl md:text-7xl font-black tracking-tighter text-white">0</span>
                    <span className="text-3xl md:text-3xl font-black tracking-tighter leading-none text-white mb-2 md:mb-2">€</span>
                  </div>
                  <div className="flex flex-col mt-2">
                    <span className="text-[#F5F3EE]/40 font-bold text-[10px] md:text-xs tracking-widest uppercase">Gratuit à vie</span>
                  </div>
                </div>
                <div>
                  <span className="inline-flex items-center gap-1 md:gap-2 px-3 md:px-3 py-1.5 md:py-1 bg-[#F9D965]/10 text-[#F9D965] border border-[#F9D965]/20 rounded-full text-[10px] md:text-[10px] font-black uppercase tracking-widest leading-tight">
                    <span className="md:hidden">🎁 30 j. Premium</span>
                    <span className="hidden md:inline">🎁 30 jours Premium offerts</span>
                  </span>
                </div>
              </div>
              <button
                onClick={() => window.location.href = `${APP_URL}/profil?mode=signup`}
                className="w-full mt-auto py-4 md:py-5 rounded-2xl md:rounded-2xl border border-white/10 bg-white/5 text-white font-black text-base md:text-lg hover:bg-white/10 hover:scale-[1.02] active:scale-[0.98] transition-all"
              >
                <span className="md:hidden">Démarrer</span>
                <span className="hidden md:inline">Démarrer l'aventure</span>
              </button>
            </div>

            {/* ── PREMIUM ── */}
            <div className={`relative rounded-[2rem] md:rounded-[3rem] border border-[#F9D965]/30 bg-white/[0.05] backdrop-blur-sm p-8 md:p-12 flex flex-col items-center text-center group/premium overflow-hidden shadow-[0_0_100px_rgba(249,217,101,0.05)] ${mobilePricingTab !== 'premium' ? 'hidden md:flex' : 'flex'}`}>
              <div className="absolute top-0 right-0 w-[30rem] h-[30rem] bg-[#F9D965]/10 blur-[100px] pointer-events-none opacity-40 group-hover/premium:opacity-60 transition-opacity duration-700" />
              <div className="mb-6 md:mb-10 relative z-10 flex-1 flex flex-col items-center">
                <div className="hidden md:flex flex-col items-center gap-2 md:gap-3 mb-6 md:mb-8">
                  <div className="w-10 h-10 md:w-14 md:h-14 rounded-2xl bg-[#F9D965]/10 border border-[#F9D965]/20 flex items-center justify-center shrink-0">
                    <Crown className="w-5 h-5 md:w-8 md:h-8 text-[#F9D965]" />
                  </div>
                  <div>
                    <p className="text-[8px] md:text-[10px] font-black uppercase tracking-[0.3em] text-[#F9D965] mb-1">
                      <span className="md:hidden">Puissance</span>
                      <span className="hidden md:inline">Plus de puissance</span>
                    </p>
                    <h3 className="text-base md:text-2xl font-black text-white leading-tight">Full Premium</h3>
                  </div>
                </div>
                <div className="flex flex-col items-center gap-1 mb-6 md:mb-6 mt-4">
                  <div className="flex items-end">
                    <span className="text-7xl md:text-7xl font-black tracking-tighter text-white">9</span>
                    <span className="text-3xl md:text-3xl font-black tracking-tighter leading-none text-white mb-2 md:mb-2">,90€</span>
                  </div>
                  <div className="flex flex-col mt-2">
                    <span className="text-[#F5F3EE]/40 font-bold text-[10px] md:text-xs tracking-widest uppercase">/mois par asso</span>
                  </div>
                </div>
                {/* Spacer invisible pour équilibrer la hauteur avec la carte Gratuite sur mobile */}
                <div className="opacity-0 pointer-events-none select-none" aria-hidden="true">
                  <span className="inline-flex items-center gap-1 md:gap-2 px-3 md:px-3 py-1.5 md:py-1 border border-transparent rounded-full text-[10px] md:text-[10px] font-black uppercase tracking-widest leading-tight">
                    <span className="md:hidden">🎁 30 j. Premium</span>
                    <span className="hidden md:inline">🎁 30 jours Premium offerts</span>
                  </span>
                </div>
              </div>
              <button
                onClick={() => window.location.href = `${APP_URL}/profil?mode=signup`}
                className="w-full relative z-10 mt-auto py-4 md:py-5 rounded-2xl md:rounded-2xl bg-[#F9D965] text-[#24574E] font-black text-base md:text-lg hover:scale-[1.02] active:scale-[0.98] transition-all shadow-[0_0_30px_rgba(249,217,101,0.2)]"
              >
                <span className="md:hidden">Premium</span>
                <span className="hidden md:inline">Démarrer Premium</span>
              </button>
            </div>
          </div>

          {/* Comparison Table - Desktop */}
          <div className="hidden md:block max-w-4xl mx-auto overflow-hidden rounded-[2rem] border border-white/10 bg-white/[0.02] backdrop-blur-md">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="p-6 text-xs font-black uppercase tracking-[0.2em] text-[#F5F3EE]/40">Fonctionnalités</th>
                  <th className="p-6 text-center text-xs font-black uppercase tracking-[0.2em] text-[#5BC0AF]">Gratuit</th>
                  <th className="p-6 text-center text-xs font-black uppercase tracking-[0.2em] text-[#F9D965]">Premium</th>
                </tr>
              </thead>
              <tbody>
                {PLAN_CATEGORIES.map((cat, idx) => (
                  <React.Fragment key={idx}>
                    <tr className="bg-white/5">
                      <td colSpan="3" className="p-4 px-6 text-[10px] font-black uppercase tracking-[0.3em] text-[#5BC0AF]">
                        {cat.module}
                      </td>
                    </tr>
                    {cat.features.map((feat, fIdx) => (
                      <tr key={fIdx} className="border-b border-white/[0.05] hover:bg-white/[0.02] transition-colors">
                        <td className="p-4 px-8 text-sm font-bold text-[#F5F3EE]/80">{feat.name}</td>
                        <td className="p-4 text-center">
                          <FeatureValue value={feat.free} color="#5BC0AF" />
                        </td>
                        <td className="p-4 text-center">
                          <FeatureValue value={feat.premium} color="#F9D965" />
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {/* Comparison Table - Mobile */}
          <div className="md:hidden max-w-4xl mx-auto rounded-[2rem] border border-white/10 bg-white/[0.02] backdrop-blur-md overflow-hidden">
            <div className="flex flex-col items-center gap-4 p-5 border-b border-white/10 bg-white/5">
              <span className="text-xs font-black uppercase tracking-[0.2em] text-[#F5F3EE]/40">Fonctionnalités</span>
              <div className="flex w-full items-center gap-4 px-3">
                <div className="flex-1 text-center">
                  <span className="text-[10px] font-black uppercase tracking-[0.2em] text-[#5BC0AF]">Gratuit</span>
                </div>
                <div className="w-px h-4 bg-white/20"></div>
                <div className="flex-1 text-center">
                  <span className="text-[10px] font-black uppercase tracking-[0.2em] text-[#F9D965]">Premium</span>
                </div>
              </div>
            </div>
            <div className="flex flex-col">
              {PLAN_CATEGORIES.map((cat, idx) => (
                <React.Fragment key={idx}>
                  <div className="bg-white/5 p-4 text-[10px] font-black uppercase tracking-[0.3em] text-[#5BC0AF] border-b border-white/10 text-center">
                    {cat.module}
                  </div>
                  {cat.features.map((feat, fIdx) => (
                    <div key={fIdx} className="flex flex-col gap-3 p-5 border-b border-white/[0.05]">
                      <div className="text-sm font-bold text-[#F5F3EE]/80 text-center">{feat.name}</div>
                      <div className="flex items-center gap-4 bg-black/20 rounded-xl p-3">
                        <div className="flex-1 flex justify-center">
                          <FeatureValue value={feat.free} color="#5BC0AF" />
                        </div>
                        <div className="w-px h-4 bg-white/10"></div>
                        <div className="flex-1 flex justify-center">
                          <FeatureValue value={feat.premium} color="#F9D965" />
                        </div>
                      </div>
                    </div>
                  ))}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Section Pourquoi SuperAsso (SEO) */}
      <section className="py-24 px-6 border-t border-white/5">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-5xl font-black mb-8 leading-tight">
            {SEO_CONTENT.whySuperAsso.title}
          </h2>
          <p className="text-xl text-[#F5F3EE]/60 font-medium leading-relaxed">
            {SEO_CONTENT.whySuperAsso.description}
          </p>
        </div>
      </section>

      {/* Section FAQ (SEO) */}
      <section id="faq" className="py-32 px-6 bg-black/10">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-[#5BC0AF] font-bold text-xs uppercase tracking-[0.3em] mb-4">FAQ</p>
            <h2 className="text-4xl md:text-5xl font-black tracking-tight mb-6">Questions Fréquentes</h2>
            <p className="text-xl text-[#F5F3EE]/60 font-medium">
              Tout ce que vous devez savoir sur SuperAsso et la gestion associative.
            </p>
          </div>

          <div className="space-y-6">
            {SEO_CONTENT.faq.slice(0, 3).map((item, i) => (
              <div key={i} className="p-8 rounded-[2rem] border border-white/10 bg-white/[0.03] backdrop-blur-sm">
                <h3 className="text-xl font-bold mb-4 text-[#5BC0AF]">{item.question}</h3>
                <p className="text-[#F5F3EE]/70 leading-relaxed font-medium">
                  {item.answer}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <Link
              to="/faq"
              className="inline-flex items-center gap-2 bg-white/10 border border-white/20 px-8 py-4 rounded-full font-bold hover:bg-white/20 transition-all group"
            >
              Voir toutes les questions fréquentes
              <ArrowRight size={20} className="transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      </section>

    </div>
  );
}

// Helper pour remonter en haut à chaque changement de page
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

/**
 * Composant pour gérer les redirections vers un domaine externe (ex: app dashboard)
 */
function ExternalRedirect({ to }) {
  useEffect(() => {
    window.location.href = to;
  }, [to]);
  return (
    <div className="min-h-screen bg-[#24574E] flex items-center justify-center">
      <div className="w-10 h-10 border-4 border-[#F9D965] border-t-transparent rounded-full animate-spin"></div>
    </div>
  );
}


function AppContent() {
  const { maintenanceMode, loading } = useRemoteConfig();
  const isBypassed = sessionStorage.getItem('maintenance_bypass') === 'true';
  const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

  if (loading) return null;

  if (maintenanceMode.active && !isBypassed && !isLocal) {
    return <MaintenancePage maintenanceMode={maintenanceMode} />;
  }

  return (
    <BrowserRouter>
      <ScrollToTop />

      <Routes>
        <Route element={<MainLayout />}>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Landing />} />
          <Route path="/docs" element={<Documentation />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/faq" element={<Faq />} />
        </Route>
        {/* /profil est désormais sur l'app dashboard */}
        <Route path="/profil" element={<ExternalRedirect to={`${APP_URL}/profil`} />} />
        <Route path="/profil/asso/:assoId" element={<ExternalRedirect to={`${APP_URL}/profil`} />} />
        {/* Pages légales : LegalLayout gère déjà sa propre nav + footer */}
        <Route path="/legal" element={<MentionsLegales />} />
        <Route path="/confidentialite" element={<Confidentialite />} />
        <Route path="/cgu" element={<CGU />} />
      </Routes>

    </BrowserRouter>
  );
}

export default function App() {
  return (
    <RemoteConfigProvider>
      <AppContent />
    </RemoteConfigProvider>
  );
}
